import { useMemo } from 'react';

export const DOTS = '...';

export interface HookProps {
  totalPageCount: number;
  siblingCount: number;
  currentPage: number;
  isMobile: boolean;
}

export function usePagination({
  totalPageCount,
  siblingCount = 1,
  currentPage = 1,
  isMobile = false,
}: HookProps) {
  const paginationRange = useMemo<Array<number | string>>(() => {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS

    const totalPageNumbers = siblingCount + (isMobile ? 4 : 7);
    const itemCount = isMobile ? 1 + siblingCount : 3 + 2 * siblingCount;

    /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount.
       Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    */
    const shouldShowLeftDots = leftSiblingIndex > (isMobile ? 1 : 2);
    const shouldShowRightDots =
      rightSiblingIndex < (isMobile ? totalPageCount : totalPageCount - 2);

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = range(1, itemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    /*
    	Case 3: No right dots to show, but left dots to be shown
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = range(totalPageCount - itemCount + 1, totalPageCount);

      return [firstPageIndex, DOTS, ...rightRange];
    }

    /*
    	Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      if (isMobile) {
        return [DOTS, currentPage, DOTS, lastPageIndex];
      }

      const middleRange = range(leftSiblingIndex, rightSiblingIndex);

      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return [];
  }, [totalPageCount, siblingCount, currentPage, isMobile]);

  return paginationRange;
}

export const range = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, idx) => idx + start);
};
